import React, { useState, useEffect } from "react";
import { UnitDTO } from "../types/unitDTO";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useParams } from "react-router-dom";
import { StrengthDTO } from "../types/strengthDTO";
import { useFetchStations } from "../hooks/StationHooks";
import { StationDTO } from "../types/stationDTO";
import { useTranslation } from 'react-i18next';
import { fi } from 'date-fns/locale';
import { registerLocale } from "react-datepicker";

registerLocale('fi', fi);

type Args = {
    headerText: string;
    dataToEdit: UnitDTO;
    submitted: (unit: UnitDTO) => void;
    deleted?: (unit: UnitDTO) => void;
};

const UnitForm = ({ headerText, dataToEdit: initialUnit, submitted, deleted }: Args) => {
    const { t, i18n } = useTranslation();
    const { organisationId } = useParams<{ organisationId: string }>();
    if (!organisationId) throw new Error("Missing organisationId from params");
    const [unitState, setUnitState] = useState(initialUnit);
    const nav = useNavigate();
    const [stations, setStations] = useState<StationDTO[]>([]);
    const { data: stationsData, isLoading: stationsIsLoading, isError: stationsIsError } = useFetchStations(organisationId);

    useEffect(() => {
        if (stationsData) {
            setStations(stationsData);
        }
    }, [stationsData]);

    const onSubmit: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
        e.preventDefault();
        submitted(unitState);
    };

    const onCancel: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
        e.preventDefault();
        nav(-1);
    }

    const onDelete: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
        e.preventDefault();
        deleted?.(unitState);
    };

    const onPriorityChange = (priority: string) => {
        const count = parseInt(priority, 10) || 0;

        setUnitState(prevState => ({
            ...prevState,
            priority: count
        }));
    };

    const onPlatoonMemberStrengthChange = (strength: string) => {
        const count = parseInt(strength, 10) || 0;

        setUnitState(prevState => ({
            ...prevState,
            strength: {
                ...prevState.strength,
                platoonLeaderCount: count
            }
        }));
    };

    const onSectionMemberStrengthChange = (strength: string) => {
        const count = parseInt(strength, 10) || 0;
        setUnitState(prevState => ({
            ...prevState,
            strength: {
                ...prevState.strength,
                sectionLeaderCount: count
            }
        }));
    };

    const onCrewMemberStrengthChange = (strength: string) => {
        const count = parseInt(strength, 10) || 0;
        setUnitState(prevState => ({
            ...prevState,
            strength: {
                ...prevState.strength,
                crewCount: count
            }
        }));
    };

    const handleStationChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = event.target.value;
        const station = stations.find(s => s.id === selectedValue)!;

        setUnitState(prevState => ({ ...prevState, station }));
    };

    const StrengthRow = ({ strength }: { strength: StrengthDTO }) => (
        <div className="form-group mt-2">
            <label htmlFor="strength">{t('unit.strength')}</label>
            <div className="form-group d-inline-block">
                <label className="ms-2" htmlFor="platoonLeaderCount">{t('unit.platoonLeaders')}</label>
                <input
                    type="number"
                    className="form-control w-auto ms-2"
                    min="0"
                    value={strength.platoonLeaderCount}
                    onChange={(e) => onPlatoonMemberStrengthChange(e.target.value)}
                />
            </div>
            +
            <div className="form-group d-inline-block">
                <label className="ms-2" htmlFor="sectionLeaderCount">{t('unit.sectionLeaders')}</label>
                <input
                    type="number"
                    className="form-control  w-auto ms-2"
                    min="0"
                    value={strength.sectionLeaderCount}
                    onChange={(e) => onSectionMemberStrengthChange(e.target.value)}
                />
            </div>                +
            <div className="form-group d-inline-block">
                <label className="ms-2" htmlFor="crewCount">{t('unit.crew')}</label>
                <input
                    type="number"
                    className="form-control  w-auto ms-2"
                    min="0"
                    value={strength.crewCount}
                    onChange={(e) => onCrewMemberStrengthChange(e.target.value)}
                />
            </div>
        </div>
    );

    if (stationsIsLoading) return <div>Loading...</div>;
    if (stationsIsError) return <div>Error</div>;

    return (
        <div className="ml-3">
            <h5 className="subtitle text-left mb-2">{headerText}</h5>
            <form>
                {/* <div className="form-group">
          <label htmlFor="id">Id</label>
          <input
            type="text"
            className="form-control"
            placeholder="Id"
            value={unitState.id}
            readOnly
          />
        </div>
        <div className="form-group">
          <label htmlFor="orgId">Organisation Id</label>
          <input
            type="text"
            className="form-control"
            placeholder="Organisation Id"
            value={unitState.organisationId}
            readOnly
          />
        </div> */}
                <div className="form-group">
                    <label htmlFor="name">{t('common.name')}</label>
                    <input
                        type="text"
                        maxLength={200}
                        className="form-control"
                        value={unitState.name}
                        onChange={(e) =>
                            setUnitState({ ...unitState, name: e.target.value })
                        }
                    />
                </div>
                <StrengthRow strength={unitState.strength} />
                <div className="form-group">
                    <label htmlFor="priority">{t('common.priority')}</label>
                    <input
                        type="number"
                        min="0"
                        className="form-control"
                        value={unitState.priority}
                        onChange={(e) => onPriorityChange(e.target.value) }                        
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="station">{t('unit.stationUnitBelongs')}</label>
                    <select
                        id="station"
                        className="form-control"
                        value={unitState.station?.id || ''}
                        onChange={handleStationChange}>
                        <option value="">{t('common.select')}</option>
                        {stations.sort((a, b) => a.name.localeCompare(b.name)).map((station) => (
                            <option key={station.id} value={station.id}>
                                {station.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="form-group mt-2">
                    <label htmlFor="description">{t('common.validFrom')}</label>
                    <div>
                        <DatePicker
                            className="form-control"
                            dateFormat={i18n.language === 'en' ? 'yyyy-MM-dd hh:mm' : 'dd.MM.yyyy HH:mm'}
                            locale={i18n.language === 'fi' ? 'fi' : 'en'}
                            selected={unitState.validFrom}
                            onChange={(date: Date | null) => date && setUnitState({ ...unitState, validFrom: date })}
                            showTimeSelect
                        />
                    </div>
                </div>
                <div className="form-group mt-2">
                    <label htmlFor="description">{t('common.validTo')}</label>
                    <div>
                        <DatePicker
                            className="form-control"
                            dateFormat={i18n.language === 'en' ? 'yyyy-MM-dd hh:mm' : 'dd.MM.yyyy HH:mm'}
                            locale={i18n.language === 'fi' ? 'fi' : 'en'}
                            selected={unitState.validTo}
                            onChange={(date: Date | null) => date && setUnitState({ ...unitState, validTo: date })}
                            showTimeSelect
                        />
                    </div>
                </div>
                <button
                    className="btn btn-primary mt-3 me-2"
                    disabled={!unitState.name || !unitState.station || unitState.station.name === '' || !unitState.validFrom || !unitState.validTo}
                    onClick={onSubmit}>
                    {t('buttons.save')}
                </button>
                {deleted && (
                    <button
                        className="btn btn-danger mt-3 me-2"
                        onClick={onDelete}>
                        {t('buttons.delete')}
                    </button>
                )}
                <button
                    className="btn btn-danger mt-3"
                    onClick={onCancel}>
                    {t('buttons.cancel')}
                </button>
            </form>
        </div>
    );
};

export default UnitForm;
